import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useQueryClient } from '@tanstack/react-query'
import clsx from 'clsx';
import PropertyTypes from '../types/PropertyTypes';
import React, { useCallback, useEffect, useState } from 'react'
import { Offcanvas } from 'react-bootstrap';
import { Controller, useForm } from 'react-hook-form';
import Select from 'react-select';
import { toast } from 'sonner';
import { privateApi } from '../../../api/api';
import { UpdatedPropertySchema, UpdatePropertyType } from '../schema/UpdatedPropertySchema';
import useGetProperty from '../hooks/useGetProperty';
import { useUpdateProperty } from '../hooks/useUpdateProperty';
import { z } from 'zod';
import FormError from '../../../shared/components/errors/FormError';
import FormSpinner from '../../../shared/components/spinner/FormSpinner';
import useIndexRegions from '../../../services/region/hooks/useIndexRegions';
import useIndexGroups from '../../../services/group/hooks/useIndexGroups';
import { format, parse } from 'date-fns';

const parseTimeRange = (rangeString: string | null | undefined) => {
    if (!rangeString) return { start: null, end: null };
    const parts = rangeString.split(" - ");
    if (parts.length !== 2) return { start: null, end: null };
    try {
        const start = format(parse(parts[0], "hh:mm a", new Date()), "HH:mm");
        const end = format(parse(parts[1], "hh:mm a", new Date()), "HH:mm");
        return { start, end };
    } catch (e) {
        return { start: null, end: null };
    }
}

interface UpdatePropertyDialogProps {
    show: boolean;
    onClose: () => void;
    selectedProperty: string;
}

const validationPropertyId = z.object({
    propertyId: z.string().uuid(),
});

interface BillingTypeSelectComponentProps {
    selectedCategory: string;
}

const BillingTypeSelectComponent: React.FC<BillingTypeSelectComponentProps> = ({ selectedCategory }) => {
    return (
        <div className='row mb-3 align-items-center'>
            <label
                htmlFor="property_category"
                className="col-sm-4 col-form-label d-flex justify-content-md-end"
            >
                Billing Type:
            </label>
            <div className="col-sm-8">
                <span className='form-control'>{selectedCategory}</span>
            </div>
        </div>
    );
};

const UpdatePropertyDialog: React.FC<UpdatePropertyDialogProps> = ({ show, onClose, selectedProperty }) => {
    const [stateLabel, setStateLabel] = useState<string>("");
    const [logoUrl, setLogoUrl] = useState<string | null>(null);
    const { data: regionsData } = useIndexRegions({ all: true });
    const { data: groupsData } = useIndexGroups({ all: true, include: 'company' });

    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

    const {
        register,
        handleSubmit,
        formState: { errors },
        control,
        reset,
        watch,
        setError,
        getValues,
        setValue,
    } = useForm<UpdatePropertyType>({
        resolver: zodResolver(UpdatedPropertySchema),
        defaultValues: {
            enable_cvv: 0,
        },
    });

    const { data: property, isError, isFetching, error, isSuccess } = useGetProperty(selectedProperty);

    useEffect(() => {
        if (property) {
            reset({
                name: property.data.name,
                property_type: property.data.property_type,
                preferred_currency: property.data.preferred_currency,
                time_zone: property.data.time_zone,
                primary_email: property.data.primary_email,
                accounting_email: property.data.accounting_email,
                email_autofill: property.data.email_autofill,
                cc_emails: property.data.cc_emails,
                mobile_number_country_code: property.data.mobile_number_country_code,
                mobile_number: property.data.mobile_number,
                primary_phone_country_code: property.data.primary_phone_country_code,
                primary_phone: property.data.primary_phone,
                website: property.data.website,
                country: property.data.country,
                state: property.data.state,
                city: property.data.city,
                street_address: property.data.street_address,
                postal_code: property.data.postal_code,
                enable_cvv: property.data.enable_cvv,
                term_condition: property.data.term_condition ?? null,
                cancellation_policy: property.data.cancellation_policy ?? null,
                region_id: property.data.region_id ?? null,
                // Property seharusnya cuma punya 1 group — kalau data lama kebetulan
                // punya lebih dari 1 (belum dibereskan), ambil yang pertama saja;
                // menyimpan form ini akan otomatis merapikannya jadi cuma 1.
                group_id: property.data.groups?.[0]?.id ?? null,
                checkin_time_start: parseTimeRange(property.data.checkin_time).start,
                checkin_time_end: parseTimeRange(property.data.checkin_time).end,
                checkout_time_start: parseTimeRange(property.data.checkout_time).start,
                checkout_time_end: parseTimeRange(property.data.checkout_time).end,
            })
            setLogoUrl(property.data.logo_url ?? "")
            setStateLabel(property.data.state ?? "")

            const initialType = PropertyTypes.find(type => type.value === property.data.property_type);
            if (initialType) {
                setSelectedCategory(initialType.category);
            }
        }
    }, [property, reset]);

    const getCcEmails = () => {
        const ccEmails = getValues("cc_emails");
        return ccEmails ? ccEmails.join(", ") : "";
    };

    if (isError) {
        toast.error(error?.message, {
            id: 'update-property',
        });
    }

    const handleClose = () => {
        reset()
        onClose()
    }

    const { mutate, isPending } = useUpdateProperty();

    const queryClient = useQueryClient();

    const onSubmit = useCallback((values: UpdatePropertyType) => {
        toast.loading('Updating property...', { id: 'update-property' });

        const validationIdResult = validationPropertyId.safeParse({ propertyId: selectedProperty });

        if (!validationIdResult.success) {
            toast.error('Invalid property id', {
                id: 'update-property',
            });
            return;
        }

        mutate({
            propertyId: validationIdResult.data.propertyId,
            formData: values,
        }, {
            onSuccess: async () => {
                handleClose();

                await queryClient.refetchQueries({
                    predicate: (query) => query.queryKey.some((key) => ['properties', 'property'].includes(key as string)),
                });

                toast.success('Property updated successfully', { id: 'update-property' });
            },
            onError: (mutationError) => {
                if ((mutationError as any)?.status === 422) {
                    const validationErrors = (mutationError as any)?.response?.data?.errors;
                    if (validationErrors) {
                        for (const [field, messages] of Object.entries(validationErrors)) {
                            setError(field as keyof UpdatePropertyType, {
                                type: "manual",
                                message: (messages as string[])[0],
                            });
                        }
                        toast.error('Failed to update property', { id: 'update-property' });
                    }
                } else {
                    toast.error((mutationError as any)?.response?.data?.message || 'Failed to update property', { id: 'update-property' });
                }
            }
        });
    }, [mutate, selectedProperty])

    return (
        <Offcanvas show={show} onHide={handleClose} placement='end' className="offcanvas-responsive">
            <Offcanvas.Header closeButton>
                <Offcanvas.Title>Update property</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
                {isError && <FormError />}
                {isFetching && <FormSpinner />}
                {isSuccess && (
                    <form
                        className="d-flex flex-column"
                        style={{ height: "100%" }}
                        onSubmit={handleSubmit(onSubmit)}
                    >
                        <div className="action-sheet-content flex-grow-1">
                            <div className="row mb-3 align-items-center">
                                <label
                                    htmlFor="name"
                                    className="col-sm-4 col-form-label d-flex justify-content-md-end"
                                >
                                    Name:
                                </label>
                                <div className="col-sm-8">
                                    <input
                                        type="text"
                                        className={clsx(
                                            "form-control app-form-control rounded-1",
                                            errors.name && "is-invalid"
                                        )}
                                        id="name"
                                        placeholder="Name"
                                        {...register("name")}
                                    />
                                    {errors.name && (
                                        <div className="invalid-feedback">{errors.name.message}</div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* <PropertyTypesSelectComponent control={control} errors={errors} /> */}

                        <PropertyTypesSelectComponent
                            control={control}
                            errors={errors}
                            onSelectedBillingType={setSelectedCategory}
                        />

                        {selectedCategory && (
                            <BillingTypeSelectComponent
                                selectedCategory={selectedCategory}
                            />
                        )}

                        <PreferredCurrencySelectComponent control={control} errors={errors} />

                        <TimezoneSelectComponent control={control} errors={errors} />

                        <div className="row mb-3 align-items-center">
                            <label
                                className="col-sm-4 col-form-label d-flex justify-content-md-end"
                            >
                                Check-in Time:
                            </label>
                            <div className="col-sm-8 d-flex align-items-center gap-2">
                                <div>
                                    <input
                                        type="time"
                                        className={clsx(
                                            "form-control app-form-control rounded-1",
                                            errors.checkin_time_start && "is-invalid"
                                        )}
                                        {...register("checkin_time_start")}
                                    />
                                    {errors.checkin_time_start && (
                                        <div className="invalid-feedback d-block">{errors.checkin_time_start.message}</div>
                                    )}
                                </div>
                                <span>-</span>
                                <div>
                                    <input
                                        type="time"
                                        className={clsx(
                                            "form-control app-form-control rounded-1",
                                            errors.checkin_time_end && "is-invalid"
                                        )}
                                        {...register("checkin_time_end")}
                                    />
                                    {errors.checkin_time_end && (
                                        <div className="invalid-feedback d-block">{errors.checkin_time_end.message}</div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="row mb-3 align-items-center">
                            <label
                                className="col-sm-4 col-form-label d-flex justify-content-md-end"
                            >
                                Check-out Time:
                            </label>
                            <div className="col-sm-8 d-flex align-items-center gap-2">
                                <div>
                                    <input
                                        type="time"
                                        className={clsx(
                                            "form-control app-form-control rounded-1",
                                            errors.checkout_time_start && "is-invalid"
                                        )}
                                        {...register("checkout_time_start")}
                                    />
                                    {errors.checkout_time_start && (
                                        <div className="invalid-feedback d-block">{errors.checkout_time_start.message}</div>
                                    )}
                                </div>
                                <span>-</span>
                                <div>
                                    <input
                                        type="time"
                                        className={clsx(
                                            "form-control app-form-control rounded-1",
                                            errors.checkout_time_end && "is-invalid"
                                        )}
                                        {...register("checkout_time_end")}
                                    />
                                    {errors.checkout_time_end && (
                                        <div className="invalid-feedback d-block">{errors.checkout_time_end.message}</div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="row mb-3 align-items-center">
                            <label
                                htmlFor="primary_email"
                                className="col-sm-4 col-form-label d-flex justify-content-md-end"
                            >
                                Primary Email:
                            </label>
                            <div className="col-sm-8">
                                <input
                                    type="email"
                                    className={clsx(
                                        "form-control app-form-control rounded-1",
                                        errors.primary_email && "is-invalid"
                                    )}
                                    id="primary_email"
                                    placeholder="Primary Email"
                                    {...register("primary_email")}
                                />
                                {errors.primary_email && (
                                    <div className="invalid-feedback">{errors.primary_email.message}</div>
                                )}
                            </div>
                        </div>

                        <CCEmailsComponent control={control} errors={errors} getValues={getCcEmails} />

                        {/* accounting_email */}
                        <div className="row mb-3 align-items-center">
                            <label
                                htmlFor="accounting_email"
                                className="col-sm-4 col-form-label d-flex justify-content-md-end"
                            >
                                Accounting Email:
                            </label>
                            <div className="col-sm-8">
                                <input
                                    type="email"
                                    className={clsx(
                                        "form-control app-form-control rounded-1",
                                        errors.accounting_email && "is-invalid"
                                    )}
                                    id="accounting_email"
                                    placeholder="Accounting Email"
                                    {...register("accounting_email")}
                                />
                                {errors.accounting_email && (
                                    <div className="invalid-feedback">{errors.accounting_email.message}</div>
                                )}
                            </div>
                        </div>

                        <div className="row mb-3 align-items-center">
                            <label
                                htmlFor="email_autofill"
                                className="col-sm-4 col-form-label d-flex justify-content-md-end"
                            >
                                Email Autofill:
                            </label>
                            <div className="col-sm-8">
                                <input
                                    type="email"
                                    className={clsx(
                                        "form-control app-form-control rounded-1",
                                        errors.email_autofill && "is-invalid"
                                    )}
                                    id="email_autofill"
                                    placeholder="Email Autofill"
                                    {...register("email_autofill")}
                                />
                                {errors.email_autofill && (
                                    <div className="invalid-feedback">{errors.email_autofill.message}</div>
                                )}
                            </div>
                        </div>

                        <MobileNumberSelectComponent control={control} errors={errors} register={register} />

                        <PrimaryPhoneSelectComponent control={control} errors={errors} register={register} />

                        <div className="row mb-3 align-items-center">
                            <label
                                htmlFor="website"
                                className="col-sm-4 col-form-label d-flex justify-content-md-end"
                            >
                                Website:
                            </label>
                            <div className="col-sm-8">
                                <input
                                    type="text"
                                    className={clsx(
                                        "form-control app-form-control rounded-1",
                                        errors.website && "is-invalid"
                                    )}
                                    id="website"
                                    placeholder="Website"
                                    {...register("website")}
                                />
                                {errors.website && (
                                    <div className="invalid-feedback">{errors.website.message}</div>
                                )}
                            </div>
                        </div>

                        <h3 className="mt-6 border-bottom fw-normal">Location Settings</h3>

                        <CountrySelectComponent control={control} errors={errors} />

                        <RegionSelectComponent control={control} errors={errors} regionsData={regionsData} />

                        {watch("country") === "ID" && (
                            <>
                                <StateSelectComponent control={control} errors={errors} setStateValue={setStateLabel} setValue={setValue} />
                                {watch("state") && (
                                    <CitySelectComponent control={control} errors={errors} stateLabel={stateLabel} />
                                )}
                            </>
                        )}

                        <div className="row mb-3 align-items-center">
                            <label
                                htmlFor="street_address"
                                className="col-sm-4 col-form-label d-flex justify-content-md-end"
                            >
                                Street Address:
                            </label>
                            <div className="col-sm-8">
                                <input
                                    type="text"
                                    className={clsx(
                                        "form-control app-form-control rounded-1",
                                        errors.street_address && "is-invalid"
                                    )}
                                    id="street_address"
                                    placeholder="Street Address"
                                    {...register("street_address")}
                                />
                                {errors.street_address && (
                                    <div className="invalid-feedback">{errors.street_address.message}</div>
                                )}
                            </div>
                        </div>

                        <div className="row mb-3 align-items-center">
                            <label
                                htmlFor="postal_code"
                                className="col-sm-4 col-form-label d-flex justify-content-md-end"
                            >
                                Postal Code:
                            </label>
                            <div className="col-sm-8">
                                <input
                                    type="text"
                                    className={clsx(
                                        "form-control app-form-control rounded-1",
                                        errors.postal_code && "is-invalid"
                                    )}
                                    id="postal_code"
                                    placeholder="Postal Code"
                                    {...register("postal_code")}
                                />
                                {errors.postal_code && (
                                    <div className="invalid-feedback">{errors.postal_code.message}</div>
                                )}
                            </div>
                        </div>

                        <div className="row mb-3 align-items-center">
                            <label
                                htmlFor="enable_cvv"
                                className="col-sm-4 col-form-label d-flex justify-content-md-end"
                            >
                                Enable CVV:
                            </label>
                            <div className="col-sm-8">
                                <Controller
                                    name="enable_cvv"
                                    control={control}
                                    render={({ field }) => (
                                        <input
                                            type="checkbox"
                                            className={clsx(
                                                "form-check-input app-form-control rounded-1",
                                                errors.enable_cvv && "is-invalid"
                                            )}
                                            id="enable_cvv"
                                            {...field}
                                            checked={field.value === 1}
                                            onChange={(e) => field.onChange(e.target.checked ? 1 : 0)}
                                            value={field.value || 0}
                                        />
                                    )}
                                />
                                {errors.enable_cvv && (
                                    <div className="invalid-feedback">{errors.enable_cvv.message}</div>
                                )}
                            </div>
                        </div>

                        <div className="row mb-3 align-items-center">
                            <label htmlFor="logo" className="col-sm-4 col-form-label d-flex justify-content-md-end">
                                Upload Logo:
                            </label>
                            <div className="col-sm-8">
                                <Controller
                                    name="logo"
                                    control={control}
                                    render={({ field: { onChange, value } }) => {
                                        const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                                            const file = e.target.files?.[0];
                                            if (file) {
                                                onChange(file);
                                            }
                                        };

                                        // ✅ Determine what to show in preview
                                        const imagePreview =
                                            value instanceof File
                                                ? URL.createObjectURL(value) // If it's a new uploaded file
                                                : value || logoUrl || null; // If it's an existing logo_url from API

                                        return (
                                            <>
                                                <input
                                                    type="file"
                                                    className={clsx("form-control", errors.logo && "is-invalid")}
                                                    id="logo"
                                                    accept="image/*"
                                                    onChange={handleFileChange}
                                                />
                                                {errors.logo && (
                                                    <div className="invalid-feedback">{String(errors.logo.message)}</div>
                                                )}

                                                {/* ✅ Show preview (either new upload or existing image) */}
                                                {imagePreview && (
                                                    <div className="mt-2">
                                                        <img
                                                            src={imagePreview}
                                                            alt="Logo Preview"
                                                            className="img-thumbnail"
                                                            style={{ maxWidth: "150px", maxHeight: "150px" }}
                                                        />
                                                    </div>
                                                )}
                                            </>
                                        );
                                    }}
                                />
                            </div>
                        </div>

                        {/* term_condition */}
                        <div className="row mb-3 align-items-center">
                            <label
                                htmlFor="term_condition"
                                className="col-sm-4 col-form-label d-flex justify-content-md-end"
                            >
                                Terms & Conditions:
                            </label>
                            <div className="col-sm-8">
                                <textarea
                                    className={clsx(
                                        "form-control app-form-control rounded-1",
                                        errors.term_condition && "is-invalid"
                                    )}
                                    id="term_condition"
                                    placeholder="Enter terms & conditions..."
                                    rows={4}
                                    {...register("term_condition")}
                                />
                                {errors.term_condition && (
                                    <div className="invalid-feedback">{errors.term_condition.message}</div>
                                )}
                            </div>
                        </div>

                        {/* cancellation_policy */}
                        <div className="row mb-3 align-items-center">
                            <label
                                htmlFor="cancellation_policy"
                                className="col-sm-4 col-form-label d-flex justify-content-md-end"
                            >
                                Cancellation Policy:
                            </label>
                            <div className="col-sm-8">
                                <textarea
                                    className={clsx(
                                        "form-control app-form-control rounded-1",
                                        errors.cancellation_policy && "is-invalid"
                                    )}
                                    id="cancellation_policy"
                                    placeholder="Enter cancellation policy..."
                                    rows={4}
                                    {...register("cancellation_policy")}
                                />
                                {errors.cancellation_policy && (
                                    <div className="invalid-feedback">{errors.cancellation_policy.message}</div>
                                )}
                            </div>
                        </div>

                        <h3 className="mt-6 border-bottom fw-normal">Group Assignment</h3>

                        <GroupSelectComponent control={control} errors={errors} groupsData={groupsData} />

                        <div className="form-group mt-auto d-grid">
                            <button
                                type="submit"
                                className="btn btn-primary btn-block"
                                disabled={isPending}
                            >
                                {isPending ? "Submitting..." : "Update Property"}
                            </button>
                        </div>
                    </form>
                )}
            </Offcanvas.Body>
        </Offcanvas>
    )
}

export default UpdatePropertyDialog

interface PropertySelectComponentProps {
    control: any;
    errors: any;
    onSelectedBillingType?: (category: string | null) => void;
}

const PropertyTypesSelectComponent: React.FC<PropertySelectComponentProps> = ({ control, errors, onSelectedBillingType }) => {
    const PropertyTypesOptions = PropertyTypes.map((propertyType) => ({
        value: propertyType.value,
        label: propertyType.label,
        category: propertyType.category,
    }));

    return (
        <div className='row mb-3 align-items-center'>
            <label
                htmlFor="property_type"
                className="col-sm-4 col-form-label d-flex justify-content-md-end"
            >
                Property Type:
            </label>
            <div className="col-sm-8">
                <Controller
                    name="property_type"
                    control={control}
                    render={({ field }) => (
                        <Select
                            {...field}
                            options={PropertyTypesOptions}
                            placeholder="Select property type"
                            isClearable
                            className={clsx(
                                "app-form-control rounded-1",
                                errors.property_type && "is-invalid"
                            )}
                            onChange={(selectedOption) => {
                                field.onChange(selectedOption?.value);
                                if (onSelectedBillingType) {
                                    onSelectedBillingType(selectedOption?.category ?? null);
                                }
                            }}
                            value={PropertyTypesOptions.find(
                                (option) => option.value === field.value
                            )}
                        />
                    )}
                />
                {errors.property_type && (
                    <div className="invalid-feedback">{errors.property_type.message}</div>
                )}
            </div>
        </div>
    );
};

interface PreferredCurrencySelectComponentProps {
    control: any;
    errors: any;
}

const PreferredCurrencySelectComponent: React.FC<PreferredCurrencySelectComponentProps> = ({ control, errors }) => {
    // Sistem saat ini HANYA support IDR (lihat IndexAi/property-management.md) --
    // dropdown ini SENGAJA dikunci ke IDR (isDisabled + options difilter cuma
    // 1 item) sampai ada currency lain yang benar-benar didukung end-to-end
    // (payment, invoice, dsb), bukan cuma tersedia di daftar /v3/currency-list.
    const CurrencyOptions = useQuery({
        queryKey: ["currencies"],
        queryFn: () => privateApi.get('/v3/currency-list').then((res) => res.data.data
            .map((currency: { value: string, text: string }) => ({
                value: currency.value,
                label: currency.text,
            }))
            .filter((currency: { value: string }) => currency.value === 'IDR')),
    })

    return (
        <div className='row mb-3 align-items-center'>
            <label
                htmlFor="preferred_currency"
                className="col-sm-4 col-form-label d-flex justify-content-md-end"
            >
                Preferred Currency:
            </label>
            <div className="col-sm-8">
                <Controller
                    name="preferred_currency"
                    control={control}
                    render={({ field }) => (
                        <Select
                            {...field}
                            value={CurrencyOptions.data?.find(
                                (opt: { value: string; label: string }) => opt.value === field.value
                            ) ?? (field.value ? { value: field.value, label: field.value } : { value: 'IDR', label: 'IDR' })}
                            options={CurrencyOptions.data}
                            isLoading={CurrencyOptions.isFetching}
                            placeholder="Select preferred currency"
                            isDisabled
                            className={clsx(
                                "app-form-control rounded-1",
                                errors.preferred_currency && "is-invalid"
                            )}
                            onChange={(selectedOption) =>
                                field.onChange(selectedOption?.value || null)
                            }
                        />
                    )}
                />
                <small className="text-muted d-block mt-1">Currently only IDR is supported.</small>
                {errors.preferred_currency && (
                    <div className="invalid-feedback">{errors.preferred_currency.message}</div>
                )}
            </div>
        </div>
    );
};

interface TimezoneSelectComponentProps {
    control: any;
    errors: any;
}

const TimezoneSelectComponent: React.FC<TimezoneSelectComponentProps> = ({ control, errors }) => {
    const TimezoneOptions = useQuery({
        queryKey: ["timezones"],
        queryFn: () => privateApi.get('/v3/timezone-list').then((res) => res.data.data.map((timezone: { value: string, text: string }) => ({
            value: timezone.value,
            label: timezone.text,
        }))),
    })

    return (
        <div className='row mb-3 align-items-center'>
            <label
                htmlFor="time_zone"
                className="col-sm-4 col-form-label d-flex justify-content-md-end"
            >
                Timezone:
            </label>
            <div className="col-sm-8">
                <Controller
                    name="time_zone"
                    control={control}
                    render={({ field }) => (
                        <Select
                            {...field}
                            value={TimezoneOptions.data?.find(
                                (opt: { value: string; label: string }) => opt.value === field.value
                            )}
                            options={TimezoneOptions.data}
                            isLoading={TimezoneOptions.isFetching}
                            placeholder="Select timezone"
                            isClearable
                            className={clsx(
                                "app-form-control rounded-1",
                                errors.time_zone && "is-invalid"
                            )}
                            onChange={(selectedOption) => field.onChange(selectedOption?.value || null)}
                        />
                    )}
                />
                {errors.time_zone && (
                    <div className="invalid-feedback">{errors.time_zone.message}</div>
                )}
            </div>
        </div>
    );
};

interface PrimaryPhoneSelectComponentProps {
    control: any;
    errors: any;
    register: any;
}

const PrimaryPhoneSelectComponent: React.FC<PrimaryPhoneSelectComponentProps> = ({ control, errors, register }) => {
    const PhoneOptions = useQuery({
        queryKey: ["dialcode_list"],
        queryFn: () => privateApi.get('/v3/dialcode-list').then((res) => res.data.data.map((country: { value: string, text: string }) => ({
            value: country.value,
            label: country.text,
        }))),
    });

    return (
        <div className="row mb-3 align-items-center">
            <label
                htmlFor='primary_phone'
                className="col-sm-4 col-form-label d-flex justify-content-md-end"
            >
                Primary Phone
            </label>
            <div className="col-sm-4">
                <Controller
                    name="primary_phone_country_code"
                    control={control}
                    render={({ field }) => (
                        <Select
                            {...field}
                            value={PhoneOptions.data?.find(
                                (opt: { value: string; label: string }) => opt.value === field.value
                            )}
                            options={PhoneOptions.data}
                            isLoading={PhoneOptions.isFetching}
                            placeholder="+62"
                            isClearable
                            className={clsx(
                                "app-form-control rounded-1",
                                errors.primary_phone_country_code && "is-invalid"
                            )}
                            onChange={(selectedOption) => field.onChange(selectedOption?.value || null)}
                        />
                    )}
                />
            </div>
            <div className="col-sm-4">
                <input
                    type='text'
                    className={clsx("form-control app-form-control rounded-1", errors.primary_phone && "is-invalid")}
                    id='primary_phone'
                    placeholder='81234567890'
                    name='primary_phone'
                    {...register('primary_phone')}
                />
                {errors.primary_phone && (
                    <div className="invalid-feedback">{errors.primary_phone.message}</div>
                )}

                {errors.primary_phone_country_code && (
                    <div className="invalid-feedback">{errors.primary_phone_country_code.message}</div>
                )}
            </div>
        </div>
    )
};

interface MobileNumberSelectComponentProps {
    control: any;
    errors: any;
    register: any;
}

const MobileNumberSelectComponent: React.FC<MobileNumberSelectComponentProps> = ({ control, errors, register }) => {
    const PhoneOptions = useQuery({
        queryKey: ["dialcode_list"],
        queryFn: () => privateApi.get('/v3/dialcode-list').then((res) => res.data.data.map((country: { value: string, text: string }) => ({
            value: country.value,
            label: country.text,
        }))),
    });

    return (
        <div className="row mb-3 align-items-center">
            <label
                htmlFor='mobile_number'
                className="col-sm-4 col-form-label d-flex justify-content-md-end"
            >
                Mobile Number
            </label>
            <div className="col-sm-4">
                <Controller
                    name="mobile_number_country_code"
                    control={control}
                    render={({ field }) => (
                        <Select
                            {...field}
                            value={PhoneOptions.data?.find(
                                (opt: { value: string; label: string }) => opt.value === field.value
                            )}
                            options={PhoneOptions.data}
                            isLoading={PhoneOptions.isFetching}
                            placeholder="+62"
                            isClearable
                            className={clsx(
                                "app-form-control rounded-1",
                                errors.mobile_number_country_code && "is-invalid"
                            )}
                            onChange={(selectedOption) => field.onChange(selectedOption?.value || null)}
                        />
                    )}
                />
            </div>
            <div className="col-sm-4">
                <input
                    type='text'
                    className={clsx("form-control app-form-control rounded-1", errors.mobile_number && "is-invalid")}
                    id='mobile_number'
                    placeholder='81234567890'
                    name='mobile_number'
                    {...register('mobile_number')}
                />
                {errors.mobile_number && (
                    <div className="invalid-feedback">{errors.mobile_number.message}</div>
                )}

                {errors.mobile_number_country_code && (
                    <div className="invalid-feedback">{errors.mobile_number_country_code.message}</div>
                )}
            </div>
        </div>
    )
};

interface CCEmailsComponentProps {
    control: any;
    errors: any;
    getValues: () => string;
}

const CCEmailsComponent: React.FC<CCEmailsComponentProps> = ({ control, errors, getValues }) => {
    const [rawCcEmails, setRawCcEmails] = useState<string>('');

    const parseCcEmails = (rawEmails: string) => {
        // Pisahkan berdasarkan spasi, koma, atau newline
        return rawEmails
            .split(/[\s,]+/) // Memisahkan berdasarkan spasi, koma, atau newline
            .map((email) => email.trim()) // Hilangkan spasi ekstra di sekitar email
            .filter((email) => email); // Hapus elemen kosong
    };

    useEffect(() => {
        if (rawCcEmails === '' || rawCcEmails === null) {
            setRawCcEmails(getValues());
        }
    }, [getValues]);

    return (
        <div className="row mb-3 align-items-center">
            <label
                htmlFor="cc_emails"
                className="col-sm-4 col-form-label d-flex justify-content-md-end"
            >
                CC Emails:
            </label>
            <div className="col-sm-8">
                <Controller
                    name="cc_emails"
                    control={control}
                    render={({ field }) => {
                        return (
                            <>
                                <textarea
                                    {...field}
                                    className={clsx(
                                        "form-control app-form-control rounded-1",
                                        errors.cc_emails && "is-invalid"
                                    )}
                                    id="cc_emails"
                                    placeholder="Enter multiple emails, separated by commas or new lines"
                                    value={rawCcEmails}
                                    onChange={(e) => {
                                        const rawValue = e.target.value;
                                        setRawCcEmails(rawValue);
                                        field.onChange(parseCcEmails(rawValue)); // Menghubungkan dengan React Hook Form
                                    }}
                                    rows={3}
                                />
                                {errors.cc_emails && (
                                    <div className="invalid-feedback">{errors.cc_emails.message}</div>
                                )}
                            </>
                        )
                    }}
                />
            </div>
        </div>
    );
};

interface CountrySelectComponentProps {
    control: any;
    errors: any;
}

const CountrySelectComponent: React.FC<CountrySelectComponentProps> = ({ control, errors }) => {
    const CountryOptions = useQuery({
        queryKey: ["countries"],
        queryFn: () => privateApi.get('/v3/country-list').then((res) => res.data.data.map((country: { value: string, text: string }) => ({
            value: country.value,
            label: country.text,
        }))),
    });

    return (
        <div className='row mb-3 align-items-center'>
            <label
                htmlFor="country"
                className="col-sm-4 col-form-label d-flex justify-content-md-end"
            >
                Country:
            </label>
            <div className="col-sm-8">
                <Controller
                    name="country"
                    control={control}
                    defaultValue={null}
                    render={({ field }) => (
                        <Select
                            {...field}
                            value={CountryOptions.data?.find(
                                (opt: { value: string; label: string }) => opt.value === field.value
                            )}
                            options={CountryOptions.data}
                            isLoading={CountryOptions.isFetching}
                            placeholder="Select country"
                            isClearable
                            className={clsx(
                                "app-form-control rounded-1",
                                errors.country && "is-invalid"
                            )}
                            onChange={(selectedOption) => field.onChange(selectedOption?.value || null)}
                        />
                    )}
                />
                {errors.country && (
                    <div className="invalid-feedback">{errors.country.message}</div>
                )}
            </div>
        </div>
    );
};

interface StateSelectComponentProps {
    control: any;
    errors: any;
    setStateValue: (value: string) => void;
    setValue: any;
}

const StateSelectComponent: React.FC<StateSelectComponentProps> = ({ control, errors, setStateValue, setValue }) => {
    const queryClient = useQueryClient();

    const StateOptions = useQuery({
        queryKey: ["states"],
        queryFn: () => privateApi.get('/v3/provinces').then((res) => res.data.data.map((state: { id: string, name: string }) => ({
            value: state.id,
            label: state.name,
        }))),
    });

    return (
        <div className='row mb-3 align-items-center'>
            <label
                htmlFor="state"
                className="col-sm-4 col-form-label d-flex justify-content-md-end"
            >
                State:
            </label>
            <div className="col-sm-8">
                <Controller
                    name="state"
                    control={control}
                    render={({ field }) => (
                        <Select
                            {...field}
                            value={StateOptions.data?.find(
                                (opt: { value: string; label: string }) => opt.label === field.value
                            )}
                            options={StateOptions.data}
                            isLoading={StateOptions.isFetching}
                            placeholder="Select state"
                            isClearable
                            className={clsx(
                                "app-form-control rounded-1",
                                errors.state && "is-invalid"
                            )}
                            onChange={async (selectedOption) => {
                                field.onChange(selectedOption?.label || null);
                                setStateValue(selectedOption?.label || null);
                                await queryClient.refetchQueries({
                                    predicate: (query) => query.queryKey.some((key) => ['cities'].includes(key as string)),
                                }); // Refetch cities
                                setValue("city", null); // Reset city value
                            }}
                        />
                    )}
                />
                {errors.state && (
                    <div className="invalid-feedback">{errors.state.message}</div>
                )}
            </div>
        </div>
    );
};

interface CitySelectComponentProps {
    control: any;
    errors: any;
    stateLabel: any;
}

const CitySelectComponent: React.FC<CitySelectComponentProps> = ({ control, errors, stateLabel }) => {
    const [province, setProvince] = useState<string>("");
    const queryClient = useQueryClient();

    const cachedProvince = queryClient.getQueryData<{ value: string; label: string }[]>(['states']);

    const getProvinceLabel = (state: string) => {
        if (cachedProvince) {
            const provinceData = cachedProvince.find((province) => province.label === state);
            return provinceData ? provinceData.value : "";
        }
        return "";
    };

    useEffect(() => {
        if (stateLabel) {
            const provinceLabel = getProvinceLabel(stateLabel);
            if (provinceLabel !== province) {
                setProvince(provinceLabel);
            }
        }
    }, [stateLabel, cachedProvince]);

    const CityOptions = useQuery({
        queryKey: ["cities", province],
        queryFn: () => privateApi.get(`/v3/regencies/${province}`).then((res) => res.data.data.map((city: { id: string; name: string }) => {
            return {
                value: city.id,
                label: city.name,
            };
        })),
        enabled: !!province,
    });

    const selectValue = (city: string) => {
        return CityOptions.data?.find(
            (opt: { value: string; label: string }) => opt.label === city
        );
    };

    if (province === "") {
        return null; // Do nothing
    } // Do nothing

    return (
        <div className='row mb-3 align-items-center'>
            <label
                htmlFor="city"
                className="col-sm-4 col-form-label d-flex justify-content-md-end"
            >
                City:
            </label>
            {CityOptions.isSuccess && (
                <div className="col-sm-8">
                    <Controller
                        name="city"
                        control={control}
                        defaultValue={null}
                        render={({ field }) => (
                            <Select
                                {...field}
                                value={field.value ? selectValue(field.value) : null}
                                options={CityOptions.data}
                                placeholder="Select city"
                                isClearable
                                className={clsx(
                                    "app-form-control rounded-1",
                                    errors.city && "is-invalid"
                                )}
                                onChange={(selectedOption) => field.onChange(selectedOption?.label || null)}
                            />
                        )}
                    />
                    {errors.city && (
                        <div className="invalid-feedback">{errors.city.message}</div>
                    )}
                </div>
            )}
        </div>
    );
};

interface RegionSelectComponentProps {
    control: any;
    errors: any;
    regionsData: any;
}

const RegionSelectComponent: React.FC<RegionSelectComponentProps> = ({ control, errors, regionsData }) => {
    const regions = regionsData?.data ?? [];
    return (
        <div className="row mb-3 align-items-center">
            <label htmlFor="region_id" className="col-sm-4 col-form-label d-flex justify-content-md-end">Region:</label>
            <div className="col-sm-8">
                <Controller
                    name="region_id"
                    control={control}
                    render={({ field }) => (
                        <select
                            {...field}
                            id="region_id"
                            className={clsx("form-select app-form-control rounded-1", errors.region_id && "is-invalid")}
                            value={field.value ?? ""}
                            onChange={(e) => field.onChange(e.target.value === "" ? null : e.target.value)}
                        >
                            <option value="">Select Region</option>
                            {regions.map((region: any) => (
                                <option key={region.id} value={region.id}>{region.name}</option>
                            ))}
                        </select>
                    )}
                />
                {errors.region_id && <div className="invalid-feedback">{typeof errors.region_id?.message === 'string' ? errors.region_id.message : ''}</div>}
            </div>
        </div>
    );
};

interface GroupSelectComponentProps {
    control: any;
    errors: any;
    groupsData: any;
}

// Satu property hanya boleh berada di 1 group (whitelabel). Menyimpan pilihan di
// sini MENGGANTI assignment group yang lama (kalau ada), bukan menambah — lihat
// PropertyController::syncGroup di backend.
const GroupSelectComponent: React.FC<GroupSelectComponentProps> = ({ control, errors, groupsData }) => {
    const groups = groupsData?.data ?? [];
    return (
        <div className="row mb-3 align-items-center">
            <label htmlFor="group_id" className="col-sm-4 col-form-label d-flex justify-content-md-end">Group:</label>
            <div className="col-sm-8">
                <Controller
                    name="group_id"
                    control={control}
                    render={({ field }) => (
                        <select
                            {...field}
                            id="group_id"
                            className={clsx("form-select app-form-control rounded-1", errors.group_id && "is-invalid")}
                            value={field.value ?? ""}
                            onChange={(e) => field.onChange(e.target.value === "" ? null : e.target.value)}
                        >
                            <option value="">No Group</option>
                            {groups.map((group: any) => (
                                <option key={group.id} value={group.id}>
                                    {group.company?.name ? `${group.name} (${group.company.name})` : group.name}
                                </option>
                            ))}
                        </select>
                    )}
                />
                {errors.group_id && <div className="invalid-feedback">{typeof errors.group_id?.message === 'string' ? errors.group_id.message : ''}</div>}
                <div className="form-text">A property can belong to only one group (whitelabel). Changing this replaces the current assignment.</div>
            </div>
        </div>
    );
};